import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Layout from './components/Layout';
import { AuthProvider } from './auth/AuthContext';
import StatisticsPage from './pages/StatisticsPage';
import Profile from './components/Profile';
import Billing from './components/Billing';
import Settings from './components/Settings';
import Help from './components/Help';
import DocumentsPage from './pages/DocumentsPage';
import TodayPage from './pages/TodayPage';
import ArchivePage from './pages/ArchivePage';
import WorkbenchPage from './pages/WorkbenchPage';
import BrandingExplorer from './pages/BrandingExplorer';
import WelcomePage from './pages/WelcomePage';
import LearnMorePage from './pages/LearnMorePage';
import LoginPage from './pages/LoginPage';
import OnboardingPage from './pages/OnboardingPage';
import { RequireAuth, RequireOnboarded } from './components/RouteGuards';
import { AppBootstrapProvider } from './context/AppBootstrapContext';
import { maybeRenderOperatorClassificationAuditRoute } from './components/operatorClassificationAuditRouteEntry';
import { maybeRenderOperatorMailboxSyncDiagnosticsRoute } from './components/operatorMailboxSyncDiagnosticsRouteEntry';
import { maybeRenderOperatorRemediationPreviewRoute } from './components/operatorRemediationPreviewRouteEntry';
import OrganizeGeneralPage from './pages/OrganizeGeneralPage';
import SimilarItemSuggestionsPage from './pages/SimilarItemSuggestionsPage';
import ModeBuilderPage from './pages/ModeBuilderPage';
import ModeSuggestionReviewPage from './pages/ModeSuggestionReviewPage';

function PreserveSearchRedirect({ to }: { to: string }) {
  const location = useLocation();
  return <Navigate to={{ pathname: to, search: location.search }} replace />;
}

function App() {
  return (
    <AuthProvider>
      <AppBootstrapProvider>
        <Router>
          <Routes>
            <Route path="/" element={<WelcomePage />} />
            <Route path="/learn-more" element={<LearnMorePage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route
              path="/onboarding"
              element={
                <RequireAuth>
                  <OnboardingPage />
                </RequireAuth>
              }
            />

            <Route
              path="/app"
              element={
                <RequireAuth>
                  <RequireOnboarded>
                    <Layout />
                  </RequireOnboarded>
                </RequireAuth>
              }
            >
              <Route index element={<TodayPage />} />
              <Route path="today" element={<TodayPage />} />
              <Route path="workbench" element={<WorkbenchPage />} />
              <Route path="organize-general" element={<OrganizeGeneralPage />} />
              <Route path="similar-item-suggestions" element={<SimilarItemSuggestionsPage />} />
              <Route path="archive" element={<ArchivePage />} />
              <Route path="documents" element={<DocumentsPage />} />
              <Route path="statistics" element={<StatisticsPage />} />
              <Route path="profile" element={<Profile />} />
              <Route path="modes/new" element={<ModeBuilderPage />} />
              <Route path="modes/suggestions/:id" element={<ModeSuggestionReviewPage />} />
              <Route path="billing" element={<Billing />} />
              <Route path="settings" element={<Settings />} />
              <Route path="help" element={<Help />} />
              <Route path="branding" element={<BrandingExplorer />} />
              <Route
                path="operator/classification-audit"
                element={maybeRenderOperatorClassificationAuditRoute() ?? <Navigate to="/app/today" replace />}
              />
              <Route
                path="operator/mailbox-sync-diagnostics"
                element={
                  maybeRenderOperatorMailboxSyncDiagnosticsRoute() ?? (
                    <Navigate to="/app/today" replace />
                  )
                }
              />
              <Route
                path="operator/remediation-preview"
                element={
                  maybeRenderOperatorRemediationPreviewRoute() ?? (
                    <Navigate to="/app/today" replace />
                  )
                }
              />
            </Route>

            <Route path="*" element={<PreserveSearchRedirect to="/" />} />
          </Routes>
        </Router>
      </AppBootstrapProvider>
    </AuthProvider>
  );
}

export default App;
